import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(formData.email, formData.password);
      toast.success('Login successful');
      navigate(user.role === 'accountant' ? '/accountant' : '/portal');
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid email or password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-parchment flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 font-inter text-sm text-iron-grey hover:text-obsidian transition-colors mb-8"
          data-testid="back-to-home"
        >
          <ArrowLeft size={16} strokeWidth={1.5} />
          Back to home
        </Link>
        
        {/* Login Card */}
        <div className="bg-white border border-iron-grey p-8 card-shadow">
          <div className="mb-8">
            <h1 className="font-fraunces font-bold text-3xl text-obsidian mb-2">Client Login</h1>
            <p className="font-inter text-sm text-iron-grey">Access your financial portal</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="font-inter text-sm text-obsidian mb-1 block">Email</label>
              <Input 
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-parchment border-iron-grey text-obsidian placeholder:text-iron-grey"
                data-testid="login-email"
              />
            </div>
            
            <div>
              <label className="font-inter text-sm text-obsidian mb-1 block">Password</label>
              <div className="relative">
                <Input 
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="bg-parchment border-iron-grey text-obsidian placeholder:text-iron-grey pr-10"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-iron-grey hover:text-obsidian"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff size={18} strokeWidth={1.5} /> : <Eye size={18} strokeWidth={1.5} />}
                </button>
              </div>
            </div>
            
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full btn-primary mt-6"
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          
          <div className="mt-6 pt-6 border-t border-iron-grey text-center">
            <p className="font-inter text-sm text-iron-grey">
              Don't have an account?{' '}
              <Link to="/register" className="text-obsidian hover:underline" data-testid="register-link">
                Create one
              </Link>
            </p>
          </div>
        </div>
        
        {/* Brand */}
        <div className="text-center mt-8">
          <span className="text-[16px] font-bold text-obsidian tracking-widest" style={{ fontFamily: "'Glacial Indifference', sans-serif" }}>
            OAKMERE ADVISORY
          </span>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
